import { Form } from '@/components';
import { ProjectHomeStepDeadlineLookupDto } from '@/core';
import { ProjectHomeDeadlineInfo } from '@/redux';
import { Button, Modal } from 'antd';
import i18next from 'i18next';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { AnyObject } from 'yup/lib/types';

interface Props {
  onClose: () => any;
  onSubmit: (value: ProjectHomeStepDeadlineLookupDto) => void;
  value: ProjectHomeDeadlineInfo;
  selectedStep: ProjectHomeStepDeadlineLookupDto;
}
const getPreviousStep = (
  currentStepId: string,
  steps: ProjectHomeStepDeadlineLookupDto[],
) => {
  const currentStep = steps.find((x) => x.id === currentStepId)!;
  const index = steps.indexOf(currentStep);
  return index > 0 ? steps.at(index - 1) : undefined;
};

const getNextStep = (
  currentStepId: string,
  steps: ProjectHomeStepDeadlineLookupDto[],
) => {
  const currentStep = steps.find((x) => x.id === currentStepId)!;
  const index = steps.indexOf(currentStep);
  return index < steps.length - 1 ? steps[index + 1] : undefined;
};

function validateDeadlineDate(
  deadlineDate: Date | undefined,
  context: Yup.TestContext<AnyObject>,
  previousDate?: string,
  nextDate?: string,
) {
  const initialDate = context.parent.initialDate as Date | undefined;

  if (!deadlineDate) return true;
  const previousStepDate = previousDate;

  if (previousStepDate && deadlineDate < new Date(previousStepDate))
    return context.createError({
      path: context.path,
      message: i18next.t('errors.date_cannot_be_less_than_previous_date'),
    });

  if (initialDate && isEqual(initialDate, deadlineDate)) return true;

  if (nextDate && deadlineDate > new Date(nextDate))
    return context.createError({
      path: context.path,
      message: i18next.t('errors.date_cannot_be_greater_than_next_date'),
    });

  return true;
}

function getSchema(previousDate?: string, nextDate?: string) {
  return Yup.object().shape({
    deadlineDate: Yup.date()
      .test('deadlineDate', (deadlineDate, context) =>
        validateDeadlineDate(deadlineDate, context, previousDate, nextDate),
      )
      .nullable()
      .required(),
  });
}

export function HomeDeadlineEditModal(props: Props) {
  const { onClose: onCancel, value, selectedStep, onSubmit } = props;
  const { t } = useTranslation();
  const previousStep = getPreviousStep(selectedStep.id, value.steps);
  const nextStep = getNextStep(selectedStep.id, value.steps);
  return (
    <Modal
      title={t('projects.homeDeadlines.editDeadline', {
        homeNumber: value.home.homeNumber,
      })}
      visible
      onCancel={onCancel}
      footer={[
        <Button key="back" type="default" onClick={onCancel}>
          {t('cancel')}
        </Button>,
        <Form.Submit uid="task-details" key="submit" type="primary">
          {t('save')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<ProjectHomeStepDeadlineLookupDto>
        uid="task-details"
        i18n="tasks.details"
        initialValues={selectedStep}
        validationSchema={getSchema(
          previousStep?.deadlineDate,
          nextStep?.deadlineDate,
        )}
        onSubmit={onSubmit}
      >
        <Form.Date
          label={t('projects.details.deadlineName', {
            name: selectedStep.name,
          })}
          name={`deadlineDate`}
          allowClear={false}
          required
        />
      </Form.Formik>
    </Modal>
  );
}
